<template>
  <div>
    <v-container>
      <v-row>
        <v-switch
          v-model="alertNotificationsEnabled"
          label="Display alert notifications"
          @change="saveAlertNotification"
          :dense="true"
        ></v-switch>
      </v-row>

      <v-row>
        <day-night-overlay></day-night-overlay>
      </v-row>

      <v-row>
        <MultiFactorAuthenticator></MultiFactorAuthenticator>
      </v-row>

      <v-row v-if="isLatestFeatureUnlocked">
        <v-switch
          v-model="latestHomepageEnabled"
          value="latest"
          label="Use the Latest page as my homepage"
          hint="Go to the 'Latest' page rather than the map page when I sign in."
          @change="saveLandingPage"
          :dense="true"
          class="mt-0"
        ></v-switch>
      </v-row>

      <v-row>
        <v-select
          v-model="poisDistUnits"
          :items="[
            { name: 'Miles', value: 'miles' },
            { name: 'Kilometers', value: 'kilometers' },
          ]"
          item-text="name"
          item-value="value"
          :persistent-hint="true"
          @change="saveChangeDistance"
          hint="Units of Distance"
        ></v-select>
      </v-row>

      <v-row>
        <v-select
          v-model="selectedLanguage"
          :items="languages"
          item-text="name"
          item-value="value"
          :loading="languageLoading"
          :persistent-hint="true"
          @change="saveLanguage"
          hint="Translate tool in Search will convert to your selected language."
        ></v-select>
      </v-row>

      <v-btn color="success" class="mt-10" @click="resetConfirmationShow = true"
        >Reset All Settings</v-btn
      >
    </v-container>

    <ConfirmationPopup
      :show="resetConfirmationShow"
      title="Reset all settings"
      text="Are you sure you want all your settings reset to the default settings?"
      :buttonCancel="resetConfirmationBtnCancel"
      :buttonAction="resetConfirmationBtnAction"
    ></ConfirmationPopup>
    <v-row>
      <v-col>
        <HelpDesk></HelpDesk>
      </v-col>
      <v-col>
        <HyperionU></HyperionU>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import lodashSortBy from "lodash/sortBy";
import { mapGetters } from "vuex";

import { LANGUAGES } from "@/common/constants/language";
import { getLanguage } from "@/services/common/translator.service";
import {
  GET_PREFERENCES,
  SAVE_PREFERENCES,
  GET_NOTIFIER,
} from "@/store/actions.type";
import { MODULE_PREFIX_HOME, MODULE_PREFIX_USER } from "@/store";
import { NOTIFIER_TYPES } from "@/common/constants/misc";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

export default {
  name: "SettingsSelector",
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ConfirmationPopup: () => import("@/components/common/ConfirmationPopup"),
    HelpDesk: () => import("@/components/common/HelpDesk"),
    DayNightOverlay: () => import("@/components/map/DayNightOverlay"),
    HyperionU: () => import("@/components/common/HyperionU"),
    MultiFactorAuthenticator: () =>
      import("@/components/common/MultiFactorAuthenticator"),
  },
  data: () => ({
    language: null,
    languageLoading: null,
    resetConfirmationShow: false,
    alertNotificationsEnabled: false,
    latestHomepageEnabled: false,
    forceClose: null,
    poisDistUnits: null,
  }),
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
      notifier: MODULE_PREFIX_HOME + "notifier",
    }),
    isLatestFeatureUnlocked() {
      return this.featureUnlocked("latest-dashboard");
    },
    languages() {
      return lodashSortBy(LANGUAGES, "name");
    },
    defaultLanguage() {
      return getLanguage("en");
    },
    selectedLanguage: {
      get() {
        return this.language || this.defaultLanguage;
      },
      set(value) {
        this.language = getLanguage(value);
      },
    },
    resetConfirmationBtnCancel() {
      return {
        text: "Cancel",
        onClickFnc: () => {
          this.setConfirmationShow(false);
        },
      };
    },
    resetConfirmationBtnAction() {
      return {
        text: "Yes, reset my settings",
        onClickFnc: () => {
          this.setConfirmationShow(false);
          this.resetPrefs();

          this.notifier.visible = true;
          this.notifier.type = NOTIFIER_TYPES.success;
          this.notifier.message = "All of your settings have been reset!";
          this.$store.dispatch(
            MODULE_PREFIX_HOME + GET_NOTIFIER,
            this.notifier
          );
          location.reload();
          this.handlePageTrackerEvent(
            EVENT_CATEGORIES.settings,
            EVENT_ACTIONS.clicked,
            "Reset All Settings"
          );
        },
      };
    },
  },
  async mounted() {
    this.getPrefs();
  },
  methods: {
    handleClickClose() {
      this.forceClose = new Date().toISOString();
    },
    getPrefs() {
      this.$store.dispatch(MODULE_PREFIX_USER + GET_PREFERENCES);
    },
    saveChangeDistance() {
      let params = this.preferences;
      params.poisDistUnits = this.poisDistUnits;
      this.savePrefs(params);
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.settings,
        EVENT_ACTIONS.changed,
        "Distance - " + this.poisDistUnits
      );
    },
    saveAlertNotification() {
      let params = this.preferences;
      delete params.alertNotificationsEnabled;
      params.home = params.home || {};
      params.home.alertNotifications = this.alertNotificationsEnabled;
      this.savePrefs(params);
      if (this.alertNotificationsEnabled) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.settings,
          EVENT_ACTIONS.enabled,
          "Alert Notifications"
        );
      }
    },
    saveLanguage(lang) {
      const LANGUAGE = getLanguage(lang);
      this.languageLoading = true;
      let params = this.preferences;
      params.language = params.language || {};
      params.language.autoTranslateDest = LANGUAGE;
      this.savePrefs(params);

      this.notifier.visible = true;
      this.notifier.type = NOTIFIER_TYPES.success;
      this.notifier.message = `Translate tool in Search will convert to your selected language of: ${LANGUAGE.name.toUpperCase()}`;
      this.$store.dispatch(MODULE_PREFIX_HOME + GET_NOTIFIER, this.notifier);
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.settings,
        EVENT_ACTIONS.changed,
        "Language - " + LANGUAGE.name
      );
    },
    saveLandingPage(pageName) {
      let params = this.preferences;
      params.home = params.home || {};
      params.home.landingPage = pageName;

      this.savePrefs(params);
      if (pageName) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.settings,
          EVENT_ACTIONS.enabled,
          "Landing Page - " + pageName
        );
      }
    },
    savePrefs(params) {
      this.$store
        .dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, params)
        .finally(() => {
          this.languageLoading = false;
        });
    },
    resetPrefs() {
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, {});
    },
    setConfirmationShow(value) {
      this.resetConfirmationShow = value;
    },
  },
  watch: {
    preferences: {
      handler() {
        if (
          this.preferences.language &&
          this.preferences.language.autoTranslateDest
        ) {
          this.selectedLanguage = this.preferences.language.autoTranslateDest.value;
        } else {
          this.selectedLanguage = this.defaultLanguage;
        }
        if (
          this.preferences.home &&
          this.preferences.home.hasOwnProperty("alertNotifications")
        ) {
          this.alertNotificationsEnabled = this.preferences.home.alertNotifications;
        } else {
          this.alertNotificationsEnabled = true;
        }
        if (this.preferences?.home?.hasOwnProperty("landingPage")) {
          this.latestHomepageEnabled = this.preferences.home.landingPage;
        } else {
          this.latestHomepageEnabled = false;
        }
        if (this.preferences.poisDistUnits) {
          this.poisDistUnits = this.preferences.poisDistUnits;
        } else {
          this.poisDistUnits = "miles";
          this.saveChangeDistance();
        }
      },
    },
  },
};
</script>
